import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { List, ListItem } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import { TextFieldCustom } from '../helpers/formFields';
import { formatString } from './valueFunctions';
import { formatNumberThsndAndDec } from '../helpers/stringFormat';

const frequencyValues = ['year', 'same as multiple']

const AddOperatingCostTypeForm = forwardRef(({ costTypes, queryResult, formActions, formResultActions, onSubmit, onSubmitResult }, ref) => {
    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleTypeChange = (event) => {
        formik.setFieldValue('frequency', event.target.value)
    };

    const getFirstKey = (object) => {
        if (!object) {
            return null
        }

        return Object.keys(object)[0]
    }

    const initialValues = {
        id: null,
        cost_type: '',
        multiple: '',
        frequency: frequencyValues[0],
        wish: '',
    }

    const costTypeKeys = Object.keys(queryResult?.cost_type || {});
    const firstKey = costTypeKeys.length > 0 ? costTypeKeys[0] : '';

    let updatedCostPerMultiple = '';
    if (firstKey && queryResult.cost_type[firstKey]?.cost_per_multiple) {
        // Get the cost_per_multiple object
        updatedCostPerMultiple = { ...queryResult.cost_type[firstKey].cost_per_multiple };

        // Iterate over its keys and add a `name` property with the key's name
        Object.keys(updatedCostPerMultiple).forEach((type) => {
            updatedCostPerMultiple[type] = {
                ...updatedCostPerMultiple[type],
                name: type,
            };
        });
    }

    const queryValues = {
        id: queryResult?.id,
        cost_type: firstKey,
        cost_per_multiple: firstKey ? updatedCostPerMultiple : '',
        multiple: firstKey ? queryResult.cost_type[firstKey]?.multiple : '',
        frequency: firstKey ? queryResult.cost_type[firstKey]?.frequency : '',
        description: firstKey ? queryResult.cost_type[firstKey]?.description : '',
        // currency: queryResult?.cost_item?.currency || '€',
        // lower: queryResult?.cost_item?.lower || '0',
        // higher: queryResult?.cost_item?.higher || '0',
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const isLarger = (value1, value2) => {
        return parseFloat(value2.toString().replace(/,/g, '')) > parseFloat(value1.toString().replace(/,/g, ''));
    };

    const validate = (values) => {
        const errors = {};

        if (!values.cost_type) {
            errors.cost_type = 'Cost Type is needed';
        }

        return errors
    }

    const validateResult = (values) => {
        const errors = {};

        if (!values.cost_type) {
            errors.cost_type = 'Cost Type is needed';
        }

        if (queryResult.cost_type[firstKey]?.multiple_type.length > 0) {
            queryResult.cost_type[firstKey].multiple_type.forEach((type) => {
                // Check if values.cost_per_multiple exists and has a value for this type.
                if (!values.cost_per_multiple || !values.cost_per_multiple[type]) {
                    // Option: Set an error indicating that the value is required.
                    // errors.cost_per_multiple = errors.cost_per_multiple || {};
                    // errors.cost_per_multiple[type] = {
                    //     lower: 'Value is required',
                    //     higher: 'Value is required'
                    // };
                    // Optionally, you could also initialize values for further validation.
                    return;
                }

                // Ensure we have an error object for this type.
                errors.cost_per_multiple = errors.cost_per_multiple || {};
                errors.cost_per_multiple[type] = errors.cost_per_multiple[type] || {};

                if (!values.cost_per_multiple[type].name) {
                    errors.cost_per_multiple[type].name = 'Cost Sub Type is needed';
                }

                if (!isNumeric(values.cost_per_multiple[type].lower)) {
                    errors.cost_per_multiple[type].lower = 'Must be numeric value';
                } else if (!isPositive(values.cost_per_multiple[type].lower)) {
                    errors.cost_per_multiple[type].lower = 'Must be positive or zero';
                }

                if (!isNumeric(values.cost_per_multiple[type].higher)) {
                    errors.cost_per_multiple[type].higher = 'Must be numeric value';
                } else if (!isPositive(values.cost_per_multiple[type].higher)) {
                    errors.cost_per_multiple[type].higher = 'Must be positive';
                } else if (!isLarger(values.cost_per_multiple[type].lower, values.cost_per_multiple[type].higher)) {
                    errors.cost_per_multiple[type].higher = 'Must be higher than lower range';
                }
            });

            if (errors.cost_per_multiple) {
                Object.keys(errors.cost_per_multiple).forEach(key => {
                    if (Object.keys(errors.cost_per_multiple[key]).length === 0) {
                        delete errors.cost_per_multiple[key];
                    }
                });

                if (Object.keys(errors.cost_per_multiple).length === 0) {
                    delete errors.cost_per_multiple;
                }
            }
        }


        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    const formikResult = useFormik({
        initialValues: queryValues,
        validate: validateResult,
        onSubmit: onSubmitResult,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
        },
    }));

    useEffect(() => {
        let updatedCostPerMultiple = '';
        if (firstKey && queryResult.cost_type[firstKey]?.cost_per_multiple) {
            // Get the cost_per_multiple object
            updatedCostPerMultiple = { ...queryResult.cost_type[firstKey].cost_per_multiple };

            // Iterate over its keys and add a `name` property with the key's name,
            // and format the `lower` and `higher` values using formatNumberThsndAndDec.
            Object.keys(updatedCostPerMultiple).forEach((type) => {
                updatedCostPerMultiple[type] = {
                    ...updatedCostPerMultiple[type],
                    name: type,
                    lower: formatNumberThsndAndDec(updatedCostPerMultiple[type].lower),
                    higher: formatNumberThsndAndDec(updatedCostPerMultiple[type].higher),
                };
            });
        }

        if (Object.keys(queryResult).length > 0) {
            formikResult.setFieldValue("id", queryResult?.id)
            formikResult.setFieldValue("cost_type", firstKey)
            formikResult.setFieldValue("cost_per_multiple", firstKey ? updatedCostPerMultiple : '')
            formikResult.setFieldValue("multiple", firstKey ? queryResult.cost_type[firstKey]?.multiple : '')
            formikResult.setFieldValue("frequency", firstKey ? queryResult.cost_type[firstKey]?.frequency : '')
            formikResult.setFieldValue("description", firstKey ? queryResult.cost_type[firstKey]?.description : '')

            // formikResult.setFieldValue("currency", queryResult?.cost_item?.currency)
            // formikResult.setFieldValue("lower", formatNumberThsndAndDec(queryResult?.cost_item?.lower))
            // formikResult.setFieldValue("higher", formatNumberThsndAndDec(queryResult?.cost_item?.higher))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult])

    if (Object.keys(queryResult).length > 0 && formikResult.values.cost_per_multiple) {
        return (
            <Box component="form" onSubmit={formikResult.handleSubmit}>
                <Grid container columnGap={1}>
                    <Grid item xs={8}>
                        <Grid container rowGap={2} columnGap={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption">
                                    Name Cost Type
                                </Typography>
                                <TextFieldCustom
                                    name="cost_type"
                                    value={formikResult.values.cost_type}
                                    onChange={formikResult.handleChange}
                                    onBlur={formikResult.handleBlur}
                                    error={(getIn(formikResult.errors, "cost_type") && getIn(formikResult.touched, "cost_type")) && Boolean(getIn(formikResult.errors, "cost_type"))}
                                    helperText={getIn(formikResult.touched, "cost_type") && getIn(formikResult.errors, "cost_type")}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={4} />
                            {queryResult?.cost_type[getFirstKey(queryResult?.cost_type)]?.multiple_type.map((type, index) => {
                                return (
                                    <React.Fragment key={type}>
                                        <Grid item xs={4}>
                                            {index === 0 &&
                                                <Typography variant="caption">
                                                    Name Cost Sub Type
                                                </Typography>
                                            }
                                            <TextFieldCustom
                                                name={`cost_per_multiple.${type}.name`}
                                                value={formikResult.values.cost_per_multiple[type].name}
                                                onChange={formikResult.handleChange}
                                                onBlur={formikResult.handleBlur}
                                                error={(getIn(formikResult.errors, `cost_per_multiple.${type}.name`) && getIn(formikResult.touched, `cost_per_multiple.${type}.name`)) && Boolean(getIn(formikResult.errors, `cost_per_multiple.${type}.name`))}
                                                helperText={getIn(formikResult.touched, `cost_per_multiple.${type}.name`) && getIn(formikResult.errors, `cost_per_multiple.${type}.name`)}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            {index === 0 &&
                                                <Typography variant="caption">
                                                    Lower Range
                                                </Typography>
                                            }
                                            <TextFieldCustom
                                                name={`cost_per_multiple.${type}.lower`}
                                                startAdornment={formikResult.values.cost_per_multiple[type].currency}
                                                value={formikResult.values.cost_per_multiple[type].lower}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formikResult.setFieldValue(`cost_per_multiple.${type}.lower`, formattedValue || '')
                                                }}
                                                onBlur={formikResult.handleBlur}
                                                error={getIn(formikResult.errors, `cost_per_multiple.${type}.lower`) && Boolean(getIn(formikResult.errors, `cost_per_multiple.${type}.lower`))}
                                                helperText={getIn(formikResult.touched, `cost_per_multiple.${type}.lower`) && getIn(formikResult.errors, `cost_per_multiple.${type}.lower`)}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            {index === 0 &&
                                                <Typography variant="caption">
                                                    Higher Range
                                                </Typography>
                                            }
                                            <TextFieldCustom
                                                name={`cost_per_multiple.${type}.higher`}
                                                startAdornment={formikResult.values.cost_per_multiple[type].currency}
                                                value={formikResult.values.cost_per_multiple[type].higher}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formikResult.setFieldValue(`cost_per_multiple.${type}.higher`, formattedValue || '')
                                                }}
                                                onBlur={formikResult.handleBlur}
                                                error={getIn(formikResult.errors, `cost_per_multiple.${type}.higher`) && Boolean(getIn(formikResult.errors, `cost_per_multiple.${type}.higher`))}
                                                helperText={(getIn(formikResult.touched, `cost_per_multiple.${type}.lower`) || getIn(formikResult.touched, `cost_per_multiple.${type}.higher`)) && getIn(formikResult.errors, `cost_per_multiple.${type}.higher`)}
                                            />
                                        </Grid>
                                    </React.Fragment>
                                )
                            })
                            }
                            <Grid item xs={12}>
                                {formResultActions()}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={3}>
                        <Paper elevation={3} sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Description:
                            </Typography>
                            <Typography variant="caption" sx={{ mb: 1 }}>
                                {queryResult?.cost_type[Object.keys(queryResult?.cost_type)[0]]?.description}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        )
    }

    return (
        <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container columnGap={1}>
                <Grid item xs={8}>
                    <Grid container rowGap={2} columnGap={1}>
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Input Cost Type to request
                            </Typography>
                            <TextFieldCustom
                                name="cost_type"
                                value={formik.values.cost_type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={(getIn(formik.errors, "cost_type") && getIn(formik.touched, "cost_type")) && Boolean(getIn(formik.errors, "cost_type"))}
                                helperText={getIn(formik.touched, "cost_type") && getIn(formik.errors, "cost_type")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={6}>
                            <Typography variant="caption">
                                Multiple
                            </Typography>
                            <TextFieldCustom
                                name="multiple"
                                value={formik.values.multiple}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "multiple") && Boolean(getIn(formik.errors, "multiple"))}
                                helperText={getIn(formik.touched, "multiple") && getIn(formik.errors, "multiple")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption">
                                Frequency
                            </Typography>
                            <Box>
                                <Select
                                    open={open}
                                    onClose={handleClose}
                                    onOpen={handleOpen}
                                    value={formik.values.frequency}
                                    onChange={handleTypeChange}
                                    size='small'
                                >
                                    {frequencyValues.map((frequency) => (
                                        <MenuItem key={frequency} value={frequency}>
                                            {formatString(frequency)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption">
                                Specify a wish if you would like to put your request in some context
                            </Typography>
                            <TextFieldCustom
                                name="wish"
                                value={formik.values.wish}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "wish") && Boolean(getIn(formik.errors, "wish"))}
                                helperText={getIn(formik.touched, "wish") && getIn(formik.errors, "wish")}
                                fullWidth
                            />
                            <Typography variant="caption">
                                E.g. "Large company of type X producing products of type Y"
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, mb: 4 }}>
                        {formActions()}
                    </Box>
                </Grid>

                <Grid item xs={3}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Existing Cost Types:
                        </Typography>
                        <List sx={{ padding: 0, listStyleType: 'disc', listStylePosition: 'inside' }}>
                            {Object.keys(costTypes).map((cost_type, index) => (
                                <ListItem
                                    key={index}
                                    sx={{
                                        display: 'list-item',
                                        paddingY: 0.5,
                                        paddingLeft: 2,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 'normal' }}>
                                        {formatString(cost_type)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    )
})

export default AddOperatingCostTypeForm
